import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Route as RouteIcon, PlayCircle } from "lucide-react";
import { addProviderTileLayer } from "@/lib/mapProvider";

export default function RouteOptimizer() {
  const { data: pending } = useQuery({
    queryKey: ["route-orders"],
    queryFn: () => api.get("/orders?limit=50").then((r) => r.data.items),
  });
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const layersRef = useRef([]);

  const eligible = (pending || []).filter((o) => ["approved", "credit_checked", "pending", "dispatched"].includes(o.status) && o.site_lat && o.site_lng);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const run = useMutation({
    mutationFn: () => api.post("/routing/optimize", { order_ids: selected }).then((r) => r.data),
    onSuccess: (data) => setResult(data),
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  useEffect(() => {
    if (!containerRef.current || mapRef.current || containerRef.current._leaflet_id) return;
    const m = L.map(containerRef.current, { center: [18.5204, 73.8567], zoom: 9 });
    addProviderTileLayer(L, m);
    mapRef.current = m;
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !result) return;
    layersRef.current.forEach((l) => map.removeLayer(l));
    layersRef.current = [];

    const depot = L.marker([result.depot.lat, result.depot.lng], {
      icon: L.divIcon({ html: '<div style="background:#000;color:white;padding:3px 6px;border-radius:4px;font-weight:bold;font-size:10px;">DEPOT</div>', iconSize: [50, 20] }),
    }).addTo(map).bindPopup("Depot").openPopup();
    layersRef.current.push(depot);

    result.route.forEach((r) => {
      const m = L.marker([r.lat, r.lng], {
        icon: L.divIcon({ html: `<div style="background:#F97316;color:white;width:24px;height:24px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-weight:bold;">${r.sequence}</div>`, iconSize: [24, 24] }),
      }).addTo(map).bindPopup(`<b>${r.order_code}</b><br/>${r.customer}<br/>${r.site}<br/>${r.quantity}L`);
      layersRef.current.push(m);
    });

    const coords = [[result.depot.lat, result.depot.lng], ...result.route.map((r) => [r.lat, r.lng]), [result.depot.lat, result.depot.lng]];
    const line = L.polyline(coords, { color: "#F97316", weight: 4, opacity: 0.8, dashArray: "8, 6" }).addTo(map);
    layersRef.current.push(line);
    map.fitBounds(line.getBounds(), { padding: [40, 40] });
  }, [result]);

  return (
    <div className="space-y-4" data-testid="route-optimizer">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Route Optimizer</h1>
        <p className="text-sm text-muted-foreground">Select multiple orders and compute the shortest multi-stop route from the depot.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <CardTitle>Orders ({selected.length} selected)</CardTitle>
            <Button size="sm" onClick={() => run.mutate()} disabled={selected.length === 0 || run.isPending} data-testid="btn-optimize"><PlayCircle size={12} className="mr-1" /> Optimize</Button>
          </CardHeader>
          <CardContent className="max-h-[550px] space-y-2 overflow-y-auto">
            {eligible.map((o) => (
              <label key={o.id} className="flex cursor-pointer items-start gap-2 rounded border border-border/60 p-2 hover:border-primary/40">
                <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} className="mt-1" data-testid={`route-check-${o.code}`} />
                <div className="flex-1 text-xs">
                  <div className="font-mono font-bold">{o.code}</div>
                  <div>{o.customer_name}</div>
                  <div className="text-muted-foreground">{o.site_name} · {o.quantity_litres}L</div>
                </div>
              </label>
            ))}
            {eligible.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">No eligible orders (need lat/lng).</div>}
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-3">
          <Card><CardContent className="p-0"><div ref={containerRef} style={{ height: "460px", width: "100%", borderRadius: "0.5rem" }} data-testid="route-map" /></CardContent></Card>
          {result && (
            <Card>
              <CardHeader><CardTitle>Optimized route</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <KPI label="Total distance" value={`${result.total_distance_km} km`} />
                  <KPI label="Est. time" value={`${result.estimated_time_hours} hrs`} />
                  <KPI label="Est. fuel" value={`${result.estimated_fuel_litres} L`} />
                </div>
                <ol className="ml-4 list-decimal space-y-1 text-sm">
                  {result.route.map((r) => (
                    <li key={r.order_id}><b className="font-mono">{r.order_code}</b> · {r.customer} · {r.site} · <span className="text-muted-foreground">{r.leg_km} km</span></li>
                  ))}
                  <li className="text-muted-foreground">↩ Return to depot · {result.return_km} km</li>
                </ol>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

const KPI = ({ label, value }) => (
  <div className="rounded border border-border/60 p-3">
    <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-1 font-display text-xl font-black text-primary">{value}</div>
  </div>
);
